import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { Reveal } from "@/components/landing/Reveal";

const CHAPTERS = [
    {
        title: "What a vehicle service contract actually is",
        copy: "A vehicle service contract (VSC) is a protection plan that pays for covered mechanical repairs after your factory warranty ends. You pay for the contract; when a covered part fails, the contract pays the repair facility. Think of it as a budget shield against the four-figure repair that always seems to arrive at the worst time.",
    },
    {
        title: "The name game: VSC vs. \"extended warranty\"",
        copy: "Almost everything marketed as an 'extended warranty' is technically a vehicle service contract. A true warranty comes from the manufacturer and is baked into the price of a new car. A VSC is a separate product you choose to buy. The distinction matters because it tells you who's behind the promise — and whether your repair shop will honor it.",
    },
    {
        title: "What coverage usually includes",
        copy: "Entry-level plans cover the powertrain: engine, transmission, and drive axle — the failures that cost the most. Mid-tier plans add air conditioning, electrical, steering, and brakes. Top-tier 'bumper-to-bumper' contracts cover virtually everything mechanical and electronic, including hi-tech modules, safety systems, and infotainment. Every legitimate contract lists exactly what's covered; read that list, not the brochure.",
    },
    {
        title: "The upfront problem with legacy contracts",
        copy: "Traditional contracts are sold as a single large purchase — often two to four thousand dollars, sometimes rolled into your auto loan where it quietly collects interest. Worse, many call-center brands wrote contracts that dealership service departments simply stopped accepting, leaving customers with expensive paper and a claim nobody would process.",
    },
    {
        title: "The subscription difference",
        copy: "AdvantaShield works like the subscriptions you already manage: pay monthly, quarterly, or annually, and cancel anytime without penalty. There's no large upfront price, and your coverage only continues while it's earning its place in your budget. Because it was created by dealership service directors, it's accepted at the service counter — and sold through dealers, not robocalls.",
    },
    {
        title: "Waiting periods and pre-existing conditions",
        copy: "Nearly every contract in the industry includes a short exclusionary period — for AdvantaShield, coverage begins after 30 days and 1,000 miles. Problems your vehicle already had before coverage started aren't eligible until they're repaired to manufacturer specifications. This is standard across the industry and protects the program from abuse, which keeps rates fair for everyone.",
    },
    {
        title: "How a claim actually works at the counter",
        copy: "With AdvantaShield, you tell your service advisor you're covered and they start the claim right there. Eligible components and labor are approved — claims are filed and paid online — and the administrator pays the facility directly at the dealer's retail rate. You cover only your deductible, if one applies, no matter how many items are repaired on that visit.",
    },
    {
        title: "The five-question checklist before you buy",
        copy: "Before signing any contract, ask: Will my dealership's service department accept it? Does it pay the shop's retail labor rate? How are claims filed and how fast are they paid? What exactly is excluded? And can I cancel without a penalty? If the answers are vague, walk away — the right provider answers all five in plain English.",
    },
];

const CHECKLIST = [
    "Accepted at your dealership's service counter",
    "Pays retail labor rates, not sub-rates",
    "Claims filed and paid online",
    "Subscription pricing — no large upfront cost",
    "Cancel anytime, no penalty",
];

export default function BuyersGuide() {
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        trackEvent("page_view");
    }, []);

    const openQuote = () => {
        trackEvent("quote_open");
        setCheckoutOpen(true);
    };

    return (
        <SubpageShell onQuote={openQuote}>
            <Seo
                title="What Is a Vehicle Service Contract? The Plain-English Buyer's Guide | AdvantaShield"
                siteName="AdvantaShield"
                description="Extended car warranties explained in plain English: what vehicle service contracts cover, how claims work, waiting periods, and why subscription coverage beats a large upfront price."
                image="/og-image.png"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
                            { "@type": "ListItem", position: 2, name: "Buyer's Guide" },
                        ],
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Article",
                    headline:
                        "What Is a Vehicle Service Contract? The Plain-English Buyer's Guide",
                    author: { "@type": "Organization", name: "AdvantaShield" },
                    publisher: { "@type": "Organization", name: "AdvantaShield" },
                    },
                ]}
            />

            <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                    The Buyer's Guide
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                    Vehicle service contracts,{" "}
                    <span className="text-sky-400">in plain English.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    No jargon, no scare tactics. Eight short chapters on how extended
                    car warranty coverage really works — written by the people who
                    process claims at dealership service counters every day.
                </p>
            </Reveal>

            <div className="mt-16">
                {CHAPTERS.map((c, i) => (
                    <Reveal key={c.title} delay={0.05}>
                        <div
                            data-testid={`guide-chapter-${i}`}
                            className="grid grid-cols-1 gap-3 border-t border-white/10 py-9 md:grid-cols-12 md:gap-8"
                        >
                            <p className="font-mono text-sm font-medium text-sky-400 md:col-span-2">
                                / 0{i + 1}
                            </p>
                            <h2 className="font-display text-xl font-semibold tracking-tight text-white md:col-span-4 md:text-2xl">
                                {c.title}
                            </h2>
                            <p className="max-w-xl text-base leading-relaxed text-slate-400 md:col-span-6">
                                {c.copy}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.1}>
                <div className="mt-10 rounded-3xl border border-sky-400/20 bg-[#0C1019] p-8 md:p-12">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        The short version:{" "}
                        <span className="text-sky-400">what good coverage looks like</span>
                    </h2>
                    <ul className="mt-6 flex flex-col gap-3.5">
                        {CHECKLIST.map((item) => (
                            <li
                                key={item}
                                className="flex items-start gap-3 text-base text-slate-300"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                                {item}
                            </li>
                        ))}
                    </ul>
                    <button
                        data-testid="guide-quote-btn"
                        onClick={openQuote}
                        className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                    >
                        Get My Instant Quote
                        <ArrowRight className="h-5 w-5" />
                    </button>
                    <p className="mt-4 text-sm text-slate-500">
                        Under a minute. No robocalls. Ever.
                    </p>
                </div>
            </Reveal>

            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
        </SubpageShell>
    );
}
